import React, { useState } from "react";
import { Button } from "antd";
import { describeAmountDue } from "./amountDue";
import { describeBillingError } from "./billingCodes";
import { settlePayment } from "./paymentAction";
import "./index.scss";

/**
 * A payment waiting on the cardholder's bank — 3-D Secure on a renewal, or on
 * the invoice a resumed subscription opened. Confirming runs the bank's check in
 * the browser from the status's `actionClientSecret`, then reads the status
 * again. Stripe's own payment page (`hostedInvoiceUrl`) is the way through when
 * the check cannot run here, and another card is the way round it.
 */
function PaymentActionPanel({ organizationId, status, onResolved, onUseDifferentCard }) {
   const [isConfirming, setIsConfirming] = useState(false);
   const [error, setError] = useState(null);
   const [offerHostedPage, setOfferHostedPage] = useState(false);

   const hostedInvoiceUrl = status?.hostedInvoiceUrl;

   const confirmPayment = async () => {
      if (isConfirming) return;

      setIsConfirming(true);
      setError(null);

      try {
         // Asked for action by the reason alone, the status may not say so
         // itself; settling it re-reads what it needs, and pays whatever else
         // is still owed once the check has gone through.
         const settled = await settlePayment({ ...status, requiresAction: true }, organizationId);

         setOfferHostedPage(settled.needsHostedPage);
         // A refusal is the billing page's to say: the status the bank left may
         // put a different fix in this panel's place.
         onResolved(settled.status, { error: settled.error });
      } catch (failure) {
         setError(describeBillingError(failure));
      } finally {
         setIsConfirming(false);
      }
   };

   const amountSentence = describeAmountDue(status);

   return (
      <div className="billing-panel">
         {amountSentence && <p className="billing-amount-due">{amountSentence}</p>}
         <p className="billing-panel__text">
            Your bank asks you to approve this payment before it goes through. It opens your bank's
            own check — usually a code by text message or a prompt in your banking app.
         </p>

         {error && (
            <div className="billing-form__message" role="alert">
               {error}
            </div>
         )}

         {offerHostedPage && hostedInvoiceUrl && (
            <p className="billing-note">
               The check couldn't open here. You can pay on{" "}
               <a href={hostedInvoiceUrl} target="_blank" rel="noopener noreferrer">
                  Stripe's secure payment page
               </a>{" "}
               instead, then come back to this page.
            </p>
         )}

         <div className="billing-actions">
            {onUseDifferentCard && (
               <Button className="defaultButton" onClick={onUseDifferentCard} disabled={isConfirming}>
                  Use a different card
               </Button>
            )}
            <Button className="kapSurveyPrimaryButton" onClick={confirmPayment} loading={isConfirming}>
               Confirm payment
            </Button>
         </div>

         {!offerHostedPage && hostedInvoiceUrl && (
            <p className="billing-note">
               Prefer Stripe's page?{" "}
               <a href={hostedInvoiceUrl} target="_blank" rel="noopener noreferrer">
                  Pay the invoice there
               </a>
               .
            </p>
         )}
      </div>
   );
}

export default PaymentActionPanel;
